import { useState, FormEvent, ChangeEvent } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    message: '',
  });
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // エラー状態をリセット
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
    
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = (): boolean => {
    // 必須項目のチェック
    if (!formData.name.trim()) {
      setErrorMessage('担当者名を入力してください。');
      return false;
    }
    
    if (!formData.email.trim()) {
      setErrorMessage('メールアドレスを入力してください。');
      return false;
    }
    
    // メールアドレスの形式チェック
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('有効なメールアドレスを入力してください。');
      return false;
    }
    
    if (!formData.message.trim()) {
      setErrorMessage('お問い合わせ内容を入力してください。');
      return false;
    }
    
    if (formData.message.length > 500) {
      setErrorMessage('お問い合わせ内容は500文字以内で入力してください。');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 既に送信中の場合は処理しない
    if (isSubmitting) {
      return;
    }
    
    // バリデーション
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName.trim());
      formBody.append('name', formData.name.trim());
      formBody.append('email', formData.email.trim());
      formBody.append('message', formData.message.trim());

      const response = await fetch('https://readdy.ai/api/form/d61hnkn4klnl5oj67of0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          name: '',
          email: '',
          message: '',
        });
        setCharCount(0);
        
        // 成功メッセージを3秒後に非表示
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('ネットワークエラーが発生しました。インターネット接続を確認してください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-16 xl:px-24">
        <div className="text-center mb-8 sm:mb-10 md:mb-14">
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-[#333333] mb-3 tracking-wide">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-[0.8rem] sm:text-sm md:text-base text-[#333333] leading-[1.7] max-w-2xl mx-auto">
            「何から手をつければいいか分からない」そんなお悩みでも構いません。貴社の課題をお聞かせください。
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-[#f8f9fa] border border-gray-200 p-4 sm:p-6 md:p-8">
            <form data-readdy-form onSubmit={handleSubmit} id="contact-form" className="space-y-4 sm:space-y-5">
              <div>
                <label htmlFor="companyName" className="block text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-1.5">
                  会社名
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 border border-gray-300 text-[0.8rem] sm:text-sm md:text-base focus:outline-none focus:border-[#27ae60] bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-1.5">
                  担当者名 <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 border border-gray-300 text-[0.8rem] sm:text-sm md:text-base focus:outline-none focus:border-[#27ae60] bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-1.5">
                  メールアドレス <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 border border-gray-300 text-[0.8rem] sm:text-sm md:text-base focus:outline-none focus:border-[#27ae60] bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-[0.8rem] sm:text-sm md:text-base text-[#333333] mb-1.5">
                  お問い合わせ内容 <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  required
                  rows={6}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 border border-gray-300 text-[0.8rem] sm:text-sm md:text-base focus:outline-none focus:border-[#27ae60] resize-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="貴社の課題やご相談内容をお聞かせください"
                ></textarea>
                <p className="mt-1.5 text-[0.7rem] sm:text-xs text-gray-500 text-right">{charCount} / 500文字</p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 text-green-800 text-[0.8rem] sm:text-sm rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-xl mr-2"></i>
                    <span>お問い合わせを受け付けました。ありがとうございます。</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-[0.8rem] sm:text-sm rounded-lg">
                  <div className="flex items-start">
                    <i className="ri-error-warning-fill text-xl mr-2 flex-shrink-0"></i>
                    <span>{errorMessage || '送信に失敗しました。もう一度お試しください。'}</span>
                  </div>
                </div>
              )}

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 bg-[#27ae60] text-white text-[0.85rem] sm:text-sm md:text-base hover:bg-[#229954] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)] flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      送信中...
                    </>
                  ) : (
                    '送信する'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
